/**
 * Soniox Setup Tutorial Page
 */

import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { useI18n, Locale } from '@/lib/i18n';
import { localizePath } from '@/lib/locale-path';
import { useDocumentMeta } from '@/lib/document-meta';
import './tutorials.scss';

interface Translation {
  pageTitle: string;
  backLink: string;
  overview: { title: string; content: string };
  steps: { title: string; content: string }[];
  features: {
    title: string;
    sections: {
      title: string;
      content?: string;
      items: string[];
    }[];
  };
  troubleshooting: {
    title: string;
    sections: {
      title: string;
      items?: { label: string; content: string }[];
      steps?: string[];
    }[];
  };
  helpText: string;
  externalLinkText: string;
}

const ACCENT_COLOR = '#2563eb';

const translations: Record<Locale, Translation> = {
  zh: {
    pageTitle: 'Soniox 设置教程',
    backLink: '返回 AI 提供商',
    overview: {
      title: '概述',
      content: '本教程将指导您设置 Soniox 以与 Sokuji 配合使用。Soniox 是实时语音到语音翻译服务，在同一个流式 API 调用中完成 60 多种语言的转写与翻译。',
    },

    steps: [
      { title: '注册 Soniox 账户', content: '访问 <a href="https://console.soniox.com/signup" target="_blank" rel="noopener noreferrer">console.soniox.com/signup</a> 注册一个免费账户。如果您已有 Soniox 账户，可直接在 <a href="https://console.soniox.com" target="_blank" rel="noopener noreferrer">console.soniox.com</a> 登录。' },
      { title: '打开控制台', content: '登录后打开 Soniox 控制台。新账户会自带一个默认项目（“My First Project”）。' },
      { title: '进入 API Keys', content: '在控制台中打开您的项目，点击侧边栏的 <strong>API Keys</strong>（API 密钥）。' },
      { title: '创建 API Key', content: '点击 <strong>创建新密钥</strong>，为其命名（例如“Sokuji”）并确认。请立即复制密钥——它只会显示一次。' },
      { title: '在 Sokuji 中配置', content: '打开 Sokuji，在设置面板中选择 <strong>Soniox</strong> 作为 AI 提供商。粘贴您的 <strong>API Key</strong>，选择源语言（或自动检测）和目标语言，并挑选一个声音。在<strong>语音</strong>区块（快捷设置，或设置 → 高级 → 音频）中，<strong>对方听到的翻译</strong>决定您的翻译是朗读出来还是仅显示文字。' },
      { title: '测试连接', content: '点击开始会话按钮，对着麦克风说话，验证实时翻译是否正常工作。您应能看到转写文字和译文。朗读的翻译会送入虚拟麦克风给对方听；如果您自己也想听到，请在语音区块中打开<strong>我也听</strong>。' },
    ],
    features: {
      title: '功能介绍',
      sections: [
        {
          title: '什么是 Soniox？',
          content: 'Soniox 是端到端实时语音翻译服务：',
          items: [
            '实时语音到语音翻译：转写、翻译和语音合成在同一条管线中完成',
            '在同一个流式 API 调用中转写并翻译 60 多种语言，翻译不额外收费',
            '自动语言检测：将源语言设为“自动检测”，Soniox 会识别所说的语言',
            '200 个内置声音用于朗读翻译，也可以用一段简短的录音克隆声音',
          ],
        },
        {
          title: '支持的语言',
          content: '支持 60 多种语言，可在任意语言之间互译：',
          items: [
            '源语言可以指定具体语言，也可以选择“自动检测”',
            '目标语言可选 60 多种语言中的任意一种',
            '涵盖中、英、日、韩、西、法、德、意、葡、俄等众多语言',
          ],
        },
        {
          title: '模式与选项',
          items: [
            '朗读或仅文字：在语音区块中，“对方听到的翻译”把您的翻译朗读进虚拟麦克风，“我听到的翻译”把对方的翻译朗读给您听。关闭其中任何一个，那一方就只显示文字',
            '声音：在设置 → 高级 → 提供商 → 语音设置中，从 200 个内置声音里挑选，或克隆您自己的声音。打开“我听到的翻译”时会多出第二行“我听到的声音”，用来选择给您朗读对方翻译的声音：只能选内置声音，如果选了和您自己相同的声音，会改用另一个声音朗读',
            '双向翻译：在“两者”模式下，默认每个方向各运行一个 Soniox 会话（「两者」模式下共享会话：禁用）。选择“已启用”则由一个会话翻译双方并自动区分说话人，成本和延迟更低；两个会话费用约为两倍，但双方同时说话时更可靠',
            '面对面：在“两者”模式下把“对方在哪里”设为“就在身边”，两个人共用一个麦克风，各自在一只耳机里听到对方说话的翻译。面对面始终使用一个共享会话',
            '源语言与翻译模式：“自动检测”适用于翻译您自己的声音（“我”模式）。在“对方”或“两者”模式下，请指定具体的源语言——自动检测无法反向使用，不能把对方的话翻译成您的语言。',
            '无需手动设置轮次检测——Soniox 在服务端自动检测语音断点',
          ],
        },
      ],
    },
    troubleshooting: {
      title: '故障排除',
      sections: [
        {
          title: '常见问题',
          items: [
            { label: '认证错误', content: '请确认 API Key 复制无误、没有多余空格。如果密钥丢失，请在 Soniox 控制台重新创建——密钥只显示一次。' },
            { label: '连接错误', content: '检查网络连接是否正常，确保防火墙没有阻止 WebSocket 连接。如果使用代理，请确保代理支持 WebSocket。' },
            { label: '听不到翻译语音', content: '如果能看到译文却听不到声音，请检查语音区块：“对方听到的翻译”必须打开。您的翻译会送入虚拟麦克风给对方听；如果您自己也想听到，请打开“我也听”，并检查那一行选择的设备。' },
          ],
        },
        {
          title: '使用建议',
          steps: [
            '使用稳定的网络连接以获得最佳实时质量',
            '在安静的环境中使用以获得最佳语音识别效果',
            '如果自动检测识别错误，请手动指定源语言',
            '在 Soniox 控制台中定期查看用量和余额',
          ],
        },
      ],
    },
    helpText: '需要更多帮助？请查阅 Soniox 官方文档，或访问我们的 GitHub 仓库获取支持。',
    externalLinkText: '打开 Soniox 控制台',
  },
  en: {
    pageTitle: 'Soniox Setup Tutorial',
    backLink: 'Back to AI Providers',
    overview: {
      title: 'Overview',
      content: 'This tutorial will guide you through setting up Soniox for use with Sokuji. Soniox is a real-time speech-to-speech translation service that transcribes and translates across 60+ languages in a single streaming API call.',
    },

    steps: [
      { title: 'Create a Soniox Account', content: 'Go to <a href="https://console.soniox.com/signup" target="_blank" rel="noopener noreferrer">console.soniox.com/signup</a> and create a free account. If you already have a Soniox account, sign in directly at <a href="https://console.soniox.com" target="_blank" rel="noopener noreferrer">console.soniox.com</a>.' },
      { title: 'Open the Console', content: 'After signing in, open the Soniox Console. Your account starts with a default project ("My First Project").' },
      { title: 'Go to API Keys', content: 'In the Console, open your project and click the <strong>API Keys</strong> section in the sidebar.' },
      { title: 'Create an API Key', content: 'Click <strong>Create new key</strong>, give it a name (e.g. "Sokuji"), and confirm. Copy the key immediately — it is shown only once.' },
      { title: 'Configure in Sokuji', content: 'Open Sokuji and select <strong>Soniox</strong> as your AI provider in the settings panel. Paste your <strong>API Key</strong>, choose the source language (or Auto Detect) and target language, and pick a voice. In the <strong>Speech</strong> block (Quick settings, or Settings → Advanced → Audio), <strong>Translation the other side hears</strong> decides whether your translation is spoken or text only.' },
      { title: 'Test the Connection', content: 'Click the Start Session button and speak into your microphone to verify that real-time translation works. You should see the transcript and the translation. The spoken translation goes into the virtual microphone for the other side; to hear it yourself, turn on <strong>I hear it too</strong> in the Speech block.' },
    ],
    features: {
      title: 'Features',
      sections: [
        {
          title: 'What is Soniox?',
          content: 'Soniox is an end-to-end real-time speech translation service:',
          items: [
            'Real-time speech-to-speech translation: transcription, translation, and speech synthesis in one pipeline',
            'Transcribe and translate 60+ languages in the same streaming API call, with translation at no extra cost',
            'Automatic language detection: set the source to Auto Detect and Soniox identifies the spoken language',
            '200 built-in voices for the translated speech, and voices you clone from a short recording',
          ],
        },
        {
          title: 'Supported Languages',
          content: 'Supports 60+ languages with any-to-any translation between them:',
          items: [
            'The source language can be a specific language or Auto Detect',
            'The target language can be any of the 60+ languages',
            'Includes Chinese, English, Japanese, Korean, Spanish, French, German, Italian, Portuguese, Russian, and many more',
          ],
        },
        {
          title: 'Modes and Options',
          items: [
            'Spoken or text only: in the Speech block, Translation the other side hears reads your translation aloud into the virtual microphone, and Translation I hear reads the other side\'s translation aloud to you. Switch either off and that side stays text only',
            'Voices: in Settings → Advanced → Provider → Voice Settings, pick one of 200 built-in voices or clone your own. While Translation I hear is on, a second row, Voice I hear, picks the voice that reads the other side to you: built-in voices only, and if you pick your own voice there, another one reads instead',
            'Two-way translation: in "Both" mode each direction runs in its own Soniox session by default (Shared session in Both mode: Disabled). Choose Enabled to translate both sides in one session with automatic speaker separation, at lower cost and latency; two sessions cost about twice as much but cope better when both people talk at once',
            'Face-to-face: in "Both" mode, set Other side to Beside me and two people share one microphone, each hearing what the other says, translated, in one earbud. Face-to-face always uses one shared session',
            'Source language and translation mode: "Auto Detect" works when translating your own voice ("Me" mode). For "Other" or "Both" mode, pick a specific source language — auto-detect cannot be reversed to translate the other side into your language.',
            'No manual turn-detection setup — Soniox detects speech endpoints on the server',
          ],
        },
      ],
    },
    troubleshooting: {
      title: 'Troubleshooting',
      sections: [
        {
          title: 'Common Issues',
          items: [
            { label: 'Authentication Error', content: 'Verify that your API Key is copied correctly with no extra spaces. If the key was lost, create a new one in the Soniox Console — keys are shown only once.' },
            { label: 'Connection Error', content: 'Check your network connection and ensure firewalls are not blocking WebSocket connections. If using a proxy, make sure it supports WebSocket.' },
            { label: 'No Translated Audio', content: 'If you see the translation but hear nothing, check the Speech block: Translation the other side hears must be on. Your translation goes into the virtual microphone for the other side; to hear it yourself, turn on I hear it too and check the device on that row.' },
          ],
        },
        {
          title: 'Tips for Best Results',
          steps: [
            'Use a stable network connection for the best real-time quality',
            'Use in a quiet environment for the best speech recognition',
            'Set the source language explicitly if auto-detection picks the wrong language',
            'Monitor your usage and balance in the Soniox Console',
          ],
        },
      ],
    },
    helpText: 'Need more help? Visit the Soniox documentation for details or check our GitHub repository for support.',
    externalLinkText: 'Open Soniox Console',
  },
  ja: {
    pageTitle: 'Soniox セットアップチュートリアル',
    backLink: 'AI プロバイダーに戻る',
    overview: {
      title: '概要',
      content: 'このチュートリアルでは、Sokuji で使用するための Soniox のセットアップを説明します。Soniox は、1 回のストリーミング API 呼び出しで 60 以上の言語の文字起こしと翻訳を行うリアルタイム音声から音声への翻訳サービスです。',
    },

    steps: [
      { title: 'Soniox アカウントを登録', content: '<a href="https://console.soniox.com/signup" target="_blank" rel="noopener noreferrer">console.soniox.com/signup</a> にアクセスして無料アカウントを登録します。すでに Soniox アカウントをお持ちの場合は、<a href="https://console.soniox.com" target="_blank" rel="noopener noreferrer">console.soniox.com</a> で直接サインインできます。' },
      { title: 'コンソールを開く', content: 'サインイン後、Soniox コンソールを開きます。新しいアカウントにはデフォルトのプロジェクト（「My First Project」）が用意されています。' },
      { title: 'API Keys に移動', content: 'コンソールでプロジェクトを開き、サイドバーの <strong>API Keys</strong> をクリックします。' },
      { title: 'API キーを作成', content: '<strong>新しいキーを作成</strong> をクリックし、名前を付けて（例：「Sokuji」）確認します。キーは一度しか表示されないため、すぐにコピーしてください。' },
      { title: 'Sokuji で設定', content: 'Sokuji を開き、設定パネルで <strong>Soniox</strong> を AI プロバイダーとして選択します。<strong>API キー</strong> を貼り付け、ソース言語（または自動検出）とターゲット言語を選択し、音声を選びます。<strong>音声</strong>ブロック（クイック設定、または 設定 → 詳細 → オーディオ）の<strong>相手に聞こえる翻訳</strong>で、翻訳を読み上げるかテキストのみにするかを決めます。' },
      { title: '接続をテスト', content: 'セッション開始ボタンをクリックし、マイクに向かって話してリアルタイム翻訳が動作していることを確認します。文字起こしと翻訳が表示されるはずです。読み上げられた翻訳は相手向けに仮想マイクへ送られます。自分でも聞くには、音声ブロックで<strong>自分にも再生</strong>をオンにします。' },
    ],
    features: {
      title: '機能紹介',
      sections: [
        {
          title: 'Soniox とは？',
          content: 'Soniox はエンドツーエンドのリアルタイム音声翻訳サービスです：',
          items: [
            'リアルタイム音声から音声への翻訳：文字起こし、翻訳、音声合成を 1 つのパイプラインで実行',
            '同じストリーミング API 呼び出しで 60 以上の言語を文字起こしして翻訳、翻訳は追加料金なし',
            '自動言語検出：ソースを「自動検出」に設定すると、Soniox が話されている言語を識別',
            '翻訳の読み上げ用に 200 の組み込み音声、さらに短い録音から複製した音声',
          ],
        },
        {
          title: 'サポートされている言語',
          content: '60 以上の言語をサポートし、それらの間で任意の言語間翻訳が可能です：',
          items: [
            'ソース言語は特定の言語または「自動検出」を選択可能',
            'ターゲット言語は 60 以上の言語から任意に選択可能',
            '中国語、英語、日本語、韓国語、スペイン語、フランス語、ドイツ語、イタリア語、ポルトガル語、ロシア語など多数を含む',
          ],
        },
        {
          title: 'モードとオプション',
          items: [
            '読み上げまたはテキストのみ：音声ブロックで、「相手に聞こえる翻訳」はあなたの翻訳を仮想マイクへ読み上げ、「自分に聞こえる翻訳」は相手の翻訳をあなたに読み上げます。どちらかをオフにすると、その側はテキストのみになります',
            '音声：設定 → 詳細 → プロバイダー → 音声設定 で、200 の組み込み音声から選ぶか、自分の声を複製します。「自分に聞こえる翻訳」がオンのときは 2 行目の「自分に聞こえる音声」が現れ、相手の翻訳をあなたに読み上げる音声を選べます。選べるのは組み込み音声のみで、自分と同じ音声を選ぶと別の音声が読み上げます',
            '双方向翻訳：「両方」モードでは、既定で方向ごとに別々の Soniox セッションを使います（「両方」モードでセッションを共有：無効）。「有効」にすると 1 つのセッションで双方を翻訳して話者を自動で区別し、コストと遅延が下がります。2 つのセッションは料金がおよそ 2 倍ですが、2 人が同時に話すときに安定します',
            '対面：「両方」モードで「相手の場所」を「隣にいる」にすると、2 人が 1 本のマイクを共有し、それぞれ相手の発言の翻訳を片耳のイヤホンで聞きます。対面では常に 1 つの共有セッションを使います',
            'ソース言語と翻訳モード：「自動検出」は自分の声を翻訳するとき（「自分」モード）に使えます。「相手」または「両方」モードでは具体的なソース言語を選んでください。自動検出は逆方向に使えないため、相手の発言をあなたの言語に翻訳できません。',
            'ターン検出の手動設定は不要——Soniox はサーバー側で音声の区切りを検出',
          ],
        },
      ],
    },
    troubleshooting: {
      title: 'トラブルシューティング',
      sections: [
        {
          title: '一般的な問題',
          items: [
            { label: '認証エラー', content: 'API キーが余分なスペースなしで正しくコピーされているか確認してください。キーを紛失した場合は、Soniox コンソールで新しいキーを作成してください——キーは一度しか表示されません。' },
            { label: '接続エラー', content: 'ネットワーク接続を確認し、ファイアウォールが WebSocket 接続をブロックしていないことを確認してください。プロキシを使用している場合は、WebSocket がサポートされていることを確認してください。' },
            { label: '翻訳音声が聞こえない', content: '翻訳は表示されるのに何も聞こえない場合は、音声ブロックを確認してください。「相手に聞こえる翻訳」がオンである必要があります。あなたの翻訳は相手向けに仮想マイクへ送られます。自分でも聞くには「自分にも再生」をオンにし、その行のデバイスを確認してください。' },
          ],
        },
        {
          title: '最良の結果を得るために',
          steps: [
            '最高のリアルタイム品質のために安定したネットワーク接続を使用する',
            '最適な音声認識のために静かな環境で使用する',
            '自動検出が誤った言語を選択する場合は、ソース言語を手動で指定する',
            'Soniox コンソールで使用量と残高を定期的に確認する',
          ],
        },
      ],
    },
    helpText: 'さらにヘルプが必要ですか？詳細は Soniox の公式ドキュメントをご覧いただくか、GitHub リポジトリでサポートを受けてください。',
    externalLinkText: 'Soniox コンソールを開く',
  },
  ko: {
    pageTitle: 'Soniox 설정 튜토리얼',
    backLink: 'AI 제공업체로 돌아가기',
    overview: {
      title: '개요',
      content: '이 튜토리얼은 Sokuji와 함께 사용하기 위한 Soniox 설정을 안내합니다. Soniox는 단일 스트리밍 API 호출에서 60개 이상의 언어를 전사하고 번역하는 실시간 음성 대 음성 번역 서비스입니다.',
    },

    steps: [
      { title: 'Soniox 계정 등록', content: '<a href="https://console.soniox.com/signup" target="_blank" rel="noopener noreferrer">console.soniox.com/signup</a>에 접속하여 무료 계정을 등록하세요. 이미 Soniox 계정이 있다면 <a href="https://console.soniox.com" target="_blank" rel="noopener noreferrer">console.soniox.com</a>에서 직접 로그인할 수 있습니다.' },
      { title: '콘솔 열기', content: '로그인 후 Soniox 콘솔을 엽니다. 새 계정에는 기본 프로젝트("My First Project")가 제공됩니다.' },
      { title: 'API Keys로 이동', content: '콘솔에서 프로젝트를 열고 사이드바의 <strong>API Keys</strong>를 클릭합니다.' },
      { title: 'API 키 생성', content: '<strong>새 키 생성</strong>을 클릭하고 이름을 지정한 후(예: "Sokuji") 확인합니다. 키는 한 번만 표시되므로 즉시 복사하세요.' },
      { title: 'Sokuji에서 설정', content: 'Sokuji를 열고 설정 패널에서 <strong>Soniox</strong>를 AI 제공업체로 선택하세요. <strong>API 키</strong>를 붙여넣고, 소스 언어(또는 자동 감지)와 대상 언어를 선택하고, 음성을 고르세요. <strong>음성</strong> 블록(빠른 설정, 또는 설정 → 고급 → 오디오)의 <strong>상대방에게 들리는 번역</strong>이 번역을 소리내어 읽을지 텍스트로만 표시할지 정합니다.' },
      { title: '연결 테스트', content: '세션 시작 버튼을 클릭하고 마이크에 대고 말하여 실시간 번역이 작동하는지 확인하세요. 전사 텍스트와 번역이 표시되어야 합니다. 읽어 주는 번역은 상대방을 위해 가상 마이크로 보내집니다. 직접 들으려면 음성 블록에서 <strong>나도 듣기</strong>를 켜세요.' },
    ],
    features: {
      title: '기능 소개',
      sections: [
        {
          title: 'Soniox란?',
          content: 'Soniox는 엔드투엔드 실시간 음성 번역 서비스입니다:',
          items: [
            '실시간 음성 대 음성 번역: 전사, 번역, 음성 합성을 하나의 파이프라인에서 수행',
            '동일한 스트리밍 API 호출에서 60개 이상의 언어를 전사하고 번역하며, 번역은 추가 비용 없음',
            '자동 언어 감지: 소스를 "자동 감지"로 설정하면 Soniox가 말하는 언어를 식별',
            '번역을 읽어 주는 200개의 기본 제공 음성과, 짧은 녹음으로 복제하는 음성',
          ],
        },
        {
          title: '지원 언어',
          content: '60개 이상의 언어를 지원하며 언어 간 자유로운 번역이 가능합니다:',
          items: [
            '소스 언어는 특정 언어 또는 "자동 감지"를 선택 가능',
            '대상 언어는 60개 이상의 언어 중 하나를 선택 가능',
            '중국어, 영어, 일본어, 한국어, 스페인어, 프랑스어, 독일어, 이탈리아어, 포르투갈어, 러시아어 등 다수 포함',
          ],
        },
        {
          title: '모드 및 옵션',
          items: [
            '음성 또는 텍스트 전용: 음성 블록에서 "상대방에게 들리는 번역"은 내 번역을 가상 마이크로 읽어 주고, "내가 듣는 번역"은 상대방의 번역을 나에게 읽어 줍니다. 둘 중 하나를 끄면 그쪽은 텍스트로만 표시됩니다',
            '음성: 설정 → 고급 → 제공자 → 음성 설정에서 200개의 기본 제공 음성 중 하나를 고르거나 내 목소리를 복제합니다. "내가 듣는 번역"이 켜져 있으면 두 번째 행 "내가 듣는 음성"이 나타나 상대방의 번역을 나에게 읽어 줄 음성을 고릅니다. 기본 제공 음성만 고를 수 있으며, 내 음성과 같은 음성을 고르면 다른 음성이 대신 읽어 줍니다',
            '양방향 번역: "모두" 모드에서는 기본적으로 방향마다 별도의 Soniox 세션이 실행됩니다(모두 모드에서 세션 공유: 비활성화). "활성화됨"을 고르면 하나의 세션이 양쪽을 번역하고 화자를 자동으로 구분하여 비용과 지연이 줄어듭니다. 세션 두 개는 비용이 약 두 배이지만 두 사람이 동시에 말할 때 더 안정적입니다',
            '대면: "모두" 모드에서 "상대방 위치"를 "내 옆"으로 설정하면 두 사람이 마이크 하나를 함께 쓰고, 각자 상대방이 한 말의 번역을 이어폰 한쪽으로 듣습니다. 대면은 항상 하나의 공유 세션을 사용합니다',
            '소스 언어와 번역 모드: "자동 감지"는 내 목소리를 번역할 때("나" 모드) 사용할 수 있습니다. "상대방" 또는 "모두" 모드에서는 구체적인 소스 언어를 선택하세요. 자동 감지는 반대 방향으로 쓸 수 없어 상대방의 말을 내 언어로 번역할 수 없습니다.',
            '턴 감지 수동 설정 불필요 — Soniox가 서버 측에서 음성 끝점을 감지',
          ],
        },
      ],
    },
    troubleshooting: {
      title: '문제 해결',
      sections: [
        {
          title: '일반적인 문제',
          items: [
            { label: '인증 오류', content: 'API 키가 불필요한 공백 없이 올바르게 복사되었는지 확인하세요. 키를 분실한 경우 Soniox 콘솔에서 새 키를 생성하세요 — 키는 한 번만 표시됩니다.' },
            { label: '연결 오류', content: '네트워크 연결을 확인하고 방화벽이 WebSocket 연결을 차단하지 않는지 확인하세요. 프록시를 사용하는 경우 WebSocket을 지원하는지 확인하세요.' },
            { label: '번역 음성이 들리지 않음', content: '번역은 보이는데 아무것도 들리지 않으면 음성 블록을 확인하세요. "상대방에게 들리는 번역"이 켜져 있어야 합니다. 내 번역은 상대방을 위해 가상 마이크로 보내집니다. 직접 들으려면 "나도 듣기"를 켜고 그 행의 장치를 확인하세요.' },
          ],
        },
        {
          title: '최상의 결과를 위한 팁',
          steps: [
            '최고의 실시간 품질을 위해 안정적인 네트워크 연결 사용',
            '최적의 음성 인식을 위해 조용한 환경에서 사용',
            '자동 감지가 잘못된 언어를 선택하는 경우 소스 언어를 수동으로 지정',
            'Soniox 콘솔에서 사용량과 잔액을 정기적으로 확인',
          ],
        },
      ],
    },
    helpText: '더 많은 도움이 필요하신가요? 자세한 내용은 Soniox 공식 문서를 확인하시거나 GitHub 저장소에서 지원을 받으세요.',
    externalLinkText: 'Soniox 콘솔 열기',
  },
  es: {
    pageTitle: 'Tutorial de configuración de Soniox',
    backLink: 'Volver a proveedores de IA',
    overview: {
      title: 'Descripción general',
      content: 'Este tutorial le guiará en la configuración de Soniox para su uso con Sokuji. Soniox es un servicio de traducción de voz a voz en tiempo real que transcribe y traduce más de 60 idiomas en una sola llamada API de streaming.',
    },

    steps: [
      { title: 'Registre una cuenta de Soniox', content: 'Vaya a <a href="https://console.soniox.com/signup" target="_blank" rel="noopener noreferrer">console.soniox.com/signup</a> y cree una cuenta gratuita. Si ya tiene una cuenta de Soniox, inicie sesión directamente en <a href="https://console.soniox.com" target="_blank" rel="noopener noreferrer">console.soniox.com</a>.' },
      { title: 'Abra la consola', content: 'Después de iniciar sesión, abra la consola de Soniox. Su cuenta comienza con un proyecto predeterminado ("My First Project").' },
      { title: 'Vaya a API Keys', content: 'En la consola, abra su proyecto y haga clic en la sección <strong>API Keys</strong> en la barra lateral.' },
      { title: 'Cree una clave API', content: 'Haga clic en <strong>Crear nueva clave</strong>, asígnele un nombre (por ejemplo, "Sokuji") y confirme. Copie la clave inmediatamente: solo se muestra una vez.' },
      { title: 'Configure en Sokuji', content: 'Abra Sokuji y seleccione <strong>Soniox</strong> como su proveedor de IA en el panel de configuración. Pegue su <strong>clave API</strong>, elija el idioma de origen (o detección automática) y el idioma de destino, y seleccione una voz. En el bloque <strong>Voz</strong> (configuración rápida, o Configuración → Avanzado → Audio), <strong>Traducción que oye la otra parte</strong> decide si su traducción se lee en voz alta o solo se muestra como texto.' },
      { title: 'Pruebe la conexión', content: 'Haga clic en el botón Iniciar sesión y hable por el micrófono para verificar que la traducción en tiempo real funciona. Debería ver la transcripción y la traducción. La traducción hablada va al micrófono virtual para la otra parte; para oírla usted también, active <strong>Yo también la oigo</strong> en el bloque Voz.' },
    ],
    features: {
      title: 'Características',
      sections: [
        {
          title: '¿Qué es Soniox?',
          content: 'Soniox es un servicio de traducción de voz en tiempo real de extremo a extremo:',
          items: [
            'Traducción de voz a voz en tiempo real: transcripción, traducción y síntesis de voz en una sola canalización',
            'Transcribe y traduce más de 60 idiomas en la misma llamada API de streaming, sin costo adicional por la traducción',
            'Detección automática de idioma: configure el origen en detección automática y Soniox identifica el idioma hablado',
            '200 voces integradas para la traducción hablada, y voces que clona a partir de una grabación corta',
          ],
        },
        {
          title: 'Idiomas admitidos',
          content: 'Admite más de 60 idiomas con traducción de cualquier idioma a cualquier otro:',
          items: [
            'El idioma de origen puede ser un idioma específico o detección automática',
            'El idioma de destino puede ser cualquiera de los más de 60 idiomas',
            'Incluye chino, inglés, japonés, coreano, español, francés, alemán, italiano, portugués, ruso y muchos más',
          ],
        },
        {
          title: 'Modos y opciones',
          items: [
            'Hablada o solo texto: en el bloque Voz, Traducción que oye la otra parte lee su traducción en voz alta hacia el micrófono virtual, y Traducción que yo oigo le lee a usted la traducción de la otra parte. Si desactiva cualquiera de las dos, ese lado queda solo en texto',
            'Voces: en Configuración → Avanzado → Proveedor → Configuración de Voz, elija una de las 200 voces integradas o clone la suya. Mientras Traducción que yo oigo está activada, una segunda fila, Voz que yo oigo, elige la voz que le lee a usted a la otra parte: solo voces integradas, y si elige allí su propia voz, lee otra en su lugar',
            'Traducción bidireccional: en el modo "Ambos", cada dirección usa por defecto su propia sesión de Soniox (Sesión compartida en el modo Ambos: Deshabilitado). Elija Habilitado para traducir a ambas partes en una sola sesión con separación automática de hablantes, con menor costo y latencia; dos sesiones cuestan aproximadamente el doble, pero funcionan mejor cuando ambas personas hablan a la vez',
            'Cara a cara: en el modo "Ambos", ponga ¿Dónde está el otro? en A mi lado y dos personas comparten un micrófono; cada una oye en un auricular lo que dice la otra, traducido. Cara a cara siempre usa una sesión compartida',
            'Idioma de origen y modo de traducción: la detección automática funciona al traducir su propia voz (modo "Yo"). En los modos "Otro" o "Ambos", elija un idioma de origen concreto: la detección automática no se puede invertir para traducir a la otra parte a su idioma.',
            'No requiere configuración manual de detección de turnos: Soniox detecta los puntos finales del habla en el servidor',
          ],
        },
      ],
    },
    troubleshooting: {
      title: 'Solución de problemas',
      sections: [
        {
          title: 'Problemas comunes',
          items: [
            { label: 'Error de autenticación', content: 'Verifique que su clave API esté copiada correctamente sin espacios adicionales. Si perdió la clave, cree una nueva en la consola de Soniox: las claves solo se muestran una vez.' },
            { label: 'Error de conexión', content: 'Compruebe su conexión de red y asegúrese de que los firewalls no bloqueen las conexiones WebSocket. Si usa un proxy, asegúrese de que admita WebSocket.' },
            { label: 'No hay audio traducido', content: 'Si ve la traducción pero no oye nada, revise el bloque Voz: Traducción que oye la otra parte debe estar activada. Su traducción va al micrófono virtual para la otra parte; para oírla usted también, active Yo también la oigo y revise el dispositivo de esa fila.' },
          ],
        },
        {
          title: 'Consejos para mejores resultados',
          steps: [
            'Use una conexión de red estable para obtener la mejor calidad en tiempo real',
            'Use en un ambiente tranquilo para un reconocimiento de voz óptimo',
            'Establezca el idioma de origen explícitamente si la detección automática elige el idioma incorrecto',
            'Supervise su uso y saldo en la consola de Soniox',
          ],
        },
      ],
    },
    helpText: '¿Necesita más ayuda? Visite la documentación de Soniox para más detalles o consulte nuestro repositorio de GitHub para obtener soporte.',
    externalLinkText: 'Abrir la consola de Soniox',
  },
  fr: {
    pageTitle: 'Tutoriel de configuration Soniox',
    backLink: 'Retour aux fournisseurs IA',
    overview: {
      title: 'Aperçu',
      content: 'Ce tutoriel vous guidera dans la configuration de Soniox pour une utilisation avec Sokuji. Soniox est un service de traduction voix-à-voix en temps réel qui transcrit et traduit plus de 60 langues en un seul appel API de streaming.',
    },

    steps: [
      { title: 'Créez un compte Soniox', content: 'Rendez-vous sur <a href="https://console.soniox.com/signup" target="_blank" rel="noopener noreferrer">console.soniox.com/signup</a> et créez un compte gratuit. Si vous avez déjà un compte Soniox, connectez-vous directement sur <a href="https://console.soniox.com" target="_blank" rel="noopener noreferrer">console.soniox.com</a>.' },
      { title: 'Ouvrez la console', content: 'Après connexion, ouvrez la console Soniox. Votre compte démarre avec un projet par défaut (« My First Project »).' },
      { title: 'Accédez à API Keys', content: 'Dans la console, ouvrez votre projet et cliquez sur la section <strong>API Keys</strong> dans la barre latérale.' },
      { title: 'Créez une clé API', content: 'Cliquez sur <strong>Créer une nouvelle clé</strong>, donnez-lui un nom (par ex. « Sokuji ») et confirmez. Copiez la clé immédiatement : elle n\'est affichée qu\'une seule fois.' },
      { title: 'Configurez dans Sokuji', content: 'Ouvrez Sokuji et sélectionnez <strong>Soniox</strong> comme fournisseur IA dans le panneau des paramètres. Collez votre <strong>clé API</strong>, choisissez la langue source (ou détection automatique) et la langue cible, et sélectionnez une voix. Dans le bloc <strong>Voix</strong> (réglages Rapide, ou Paramètres → Avancé → Audio), <strong>Traduction entendue par l\'autre</strong> décide si votre traduction est lue à voix haute ou affichée en texte seul.' },
      { title: 'Testez la connexion', content: 'Cliquez sur le bouton Démarrer la session et parlez dans votre microphone pour vérifier que la traduction en temps réel fonctionne. Vous devriez voir la transcription et la traduction. La traduction parlée part dans le micro virtuel pour l\'autre ; pour l\'entendre vous-même, activez <strong>Je l\'entends aussi</strong> dans le bloc Voix.' },
    ],
    features: {
      title: 'Fonctionnalités',
      sections: [
        {
          title: 'Qu\'est-ce que Soniox ?',
          content: 'Soniox est un service de traduction vocale en temps réel de bout en bout :',
          items: [
            'Traduction voix-à-voix en temps réel : transcription, traduction et synthèse vocale dans un seul pipeline',
            'Transcrit et traduit plus de 60 langues dans le même appel API de streaming, sans frais supplémentaires pour la traduction',
            'Détection automatique de la langue : réglez la source sur détection automatique et Soniox identifie la langue parlée',
            '200 voix intégrées pour la traduction parlée, et des voix que vous clonez à partir d\'un court enregistrement',
          ],
        },
        {
          title: 'Langues prises en charge',
          content: 'Prend en charge plus de 60 langues avec une traduction de n\'importe quelle langue vers n\'importe quelle autre :',
          items: [
            'La langue source peut être une langue spécifique ou la détection automatique',
            'La langue cible peut être l\'une des plus de 60 langues',
            'Comprend le chinois, l\'anglais, le japonais, le coréen, l\'espagnol, le français, l\'allemand, l\'italien, le portugais, le russe et bien d\'autres',
          ],
        },
        {
          title: 'Modes et options',
          items: [
            'Parlé ou texte seul : dans le bloc Voix, Traduction entendue par l\'autre lit votre traduction à voix haute dans le micro virtuel, et Traduction que j\'entends vous lit la traduction de l\'autre. Désactivez l\'une ou l\'autre et ce côté reste en texte seul',
            'Voix : dans Paramètres → Avancé → Fournisseur → Paramètres de Voix, choisissez l\'une des 200 voix intégrées ou clonez la vôtre. Tant que Traduction que j\'entends est activée, une deuxième ligne, Voix que j\'entends, choisit la voix qui vous lit l\'autre : voix intégrées uniquement, et si vous y choisissez votre propre voix, une autre lit à sa place',
            'Traduction bidirectionnelle : en mode « Les deux », chaque sens utilise par défaut sa propre session Soniox (Session partagée en mode Les deux : Désactivé). Choisissez Activé pour traduire les deux côtés dans une seule session avec séparation automatique des locuteurs, à moindre coût et latence ; deux sessions coûtent environ deux fois plus mais tiennent mieux quand les deux personnes parlent en même temps',
            'Face à face : en mode « Les deux », réglez Où est l\'autre ? sur À côté de moi ; deux personnes partagent un microphone et chacune entend dans une oreillette ce que dit l\'autre, traduit. Le face à face utilise toujours une session partagée',
            'Langue source et mode de traduction : la détection automatique fonctionne pour traduire votre propre voix (mode « Moi »). En mode « Autre » ou « Les deux », choisissez une langue source précise : la détection automatique ne peut pas être inversée pour traduire l\'autre dans votre langue.',
            'Aucune configuration manuelle de détection de tour : Soniox détecte les points de fin de parole côté serveur',
          ],
        },
      ],
    },
    troubleshooting: {
      title: 'Dépannage',
      sections: [
        {
          title: 'Problèmes courants',
          items: [
            { label: 'Erreur d\'authentification', content: 'Vérifiez que votre clé API est copiée correctement sans espaces supplémentaires. Si la clé est perdue, créez-en une nouvelle dans la console Soniox : les clés ne sont affichées qu\'une seule fois.' },
            { label: 'Erreur de connexion', content: 'Vérifiez votre connexion réseau et assurez-vous que les pare-feu ne bloquent pas les connexions WebSocket. Si vous utilisez un proxy, assurez-vous qu\'il prend en charge WebSocket.' },
            { label: 'Pas d\'audio traduit', content: 'Si vous voyez la traduction sans rien entendre, vérifiez le bloc Voix : Traduction entendue par l\'autre doit être activée. Votre traduction part dans le micro virtuel pour l\'autre ; pour l\'entendre vous-même, activez Je l\'entends aussi et vérifiez l\'appareil de cette ligne.' },
          ],
        },
        {
          title: 'Conseils pour de meilleurs résultats',
          steps: [
            'Utilisez une connexion réseau stable pour une meilleure qualité en temps réel',
            'Utilisez dans un environnement calme pour une reconnaissance vocale optimale',
            'Définissez explicitement la langue source si la détection automatique choisit la mauvaise langue',
            'Surveillez votre utilisation et votre solde dans la console Soniox',
          ],
        },
      ],
    },
    helpText: 'Besoin d\'aide supplémentaire ? Consultez la documentation Soniox pour plus de détails ou notre dépôt GitHub pour obtenir du support.',
    externalLinkText: 'Ouvrir la console Soniox',
  },
  ar: {
    pageTitle: 'دليل إعداد Soniox',
    backLink: 'العودة إلى مزودي الذكاء الاصطناعي',
    overview: {
      title: 'نظرة عامة',
      content: 'سيرشدك هذا الدليل خلال إعداد Soniox للاستخدام مع Sokuji. Soniox هي خدمة ترجمة فورية من كلام إلى كلام تقوم بنسخ وترجمة أكثر من 60 لغة في استدعاء API واحد للبث المباشر.',
    },

    steps: [
      { title: 'سجّل حسابًا في Soniox', content: 'انتقل إلى <a href="https://console.soniox.com/signup" target="_blank" rel="noopener noreferrer">console.soniox.com/signup</a> وأنشئ حسابًا مجانيًا. إذا كان لديك حساب Soniox بالفعل، سجّل الدخول مباشرة على <a href="https://console.soniox.com" target="_blank" rel="noopener noreferrer">console.soniox.com</a>.' },
      { title: 'افتح وحدة التحكم', content: 'بعد تسجيل الدخول، افتح وحدة تحكم Soniox. يبدأ حسابك بمشروع افتراضي ("My First Project").' },
      { title: 'انتقل إلى API Keys', content: 'في وحدة التحكم، افتح مشروعك وانقر على قسم <strong>API Keys</strong> في الشريط الجانبي.' },
      { title: 'أنشئ مفتاح API', content: 'انقر على <strong>إنشاء مفتاح جديد</strong>، وامنحه اسمًا (مثل "Sokuji") وأكّد. انسخ المفتاح فورًا — فهو يُعرض مرة واحدة فقط.' },
      { title: 'قم بالتكوين في Sokuji', content: 'افتح Sokuji واختر <strong>Soniox</strong> كمزود الذكاء الاصطناعي في لوحة الإعدادات. الصق <strong>مفتاح API</strong>، واختر لغة المصدر (أو الكشف التلقائي) ولغة الهدف، واختر صوتًا. في قسم <strong>الكلام</strong> (الإعدادات السريعة، أو الإعدادات ← متقدم ← الصوت)، يحدد <strong>الترجمة التي يسمعها الطرف الآخر</strong> ما إذا كانت ترجمتك تُقرأ بصوت عالٍ أو تُعرض نصًا فقط.' },
      { title: 'اختبر الاتصال', content: 'انقر على زر بدء الجلسة وتحدث في الميكروفون للتحقق من أن الترجمة الفورية تعمل. يجب أن ترى النص المكتوب والترجمة. تذهب الترجمة المنطوقة إلى الميكروفون الافتراضي ليسمعها الطرف الآخر؛ لتسمعها أنت أيضًا، فعّل <strong>أسمعها أنا أيضًا</strong> في قسم الكلام.' },
    ],
    features: {
      title: 'الميزات',
      sections: [
        {
          title: 'ما هو Soniox؟',
          content: 'Soniox هي خدمة ترجمة صوتية فورية شاملة:',
          items: [
            'ترجمة فورية من كلام إلى كلام: النسخ والترجمة وتوليف الكلام في مسار واحد',
            'نسخ وترجمة أكثر من 60 لغة في نفس استدعاء API للبث، بدون تكلفة إضافية للترجمة',
            'كشف تلقائي للغة: اضبط المصدر على الكشف التلقائي ويحدد Soniox اللغة المنطوقة',
            '200 صوت مدمج لقراءة الترجمة، وأصوات تستنسخها من تسجيل قصير',
          ],
        },
        {
          title: 'اللغات المدعومة',
          content: 'يدعم أكثر من 60 لغة مع ترجمة من أي لغة إلى أي لغة أخرى:',
          items: [
            'يمكن أن تكون لغة المصدر لغة محددة أو الكشف التلقائي',
            'يمكن أن تكون لغة الهدف أيًا من أكثر من 60 لغة',
            'يشمل الصينية والإنجليزية واليابانية والكورية والإسبانية والفرنسية والألمانية والإيطالية والبرتغالية والروسية وغيرها الكثير',
          ],
        },
        {
          title: 'الأوضاع والخيارات',
          items: [
            'منطوقة أو نص فقط: في قسم الكلام، يقرأ خيار «الترجمة التي يسمعها الطرف الآخر» ترجمتك بصوت عالٍ في الميكروفون الافتراضي، ويقرأ خيار «الترجمة التي أسمعها» ترجمة الطرف الآخر لك. إذا أوقفت أيًّا منهما يبقى ذلك الجانب نصًا فقط',
            'الأصوات: في الإعدادات ← متقدم ← المزود ← إعدادات الصوت، اختر واحدًا من 200 صوت مدمج أو استنسخ صوتك. ما دام خيار «الترجمة التي أسمعها» مفعّلًا، يظهر صف ثانٍ هو «الصوت الذي أسمعه» لاختيار الصوت الذي يقرأ لك كلام الطرف الآخر: الأصوات المدمجة فقط، وإذا اخترت فيه صوتك نفسه يقرأ صوت آخر بدلًا منه',
            'ترجمة ثنائية الاتجاه: في وضع "الكل" يعمل كل اتجاه افتراضيًا في جلسة Soniox خاصة به (جلسة مشتركة في وضع الكل: معطل). اختر مفعل لترجمة الطرفين في جلسة واحدة مع فصل تلقائي بين المتحدثين، بتكلفة وتأخير أقل؛ تكلّف الجلستان نحو الضعف لكنهما أكثر ثباتًا عندما يتحدث الشخصان في الوقت نفسه',
            'وجهًا لوجه: في وضع "الكل"، اضبط «مكان الطرف الآخر» على «بجانبي» ليتشارك شخصان ميكروفونًا واحدًا، ويسمع كل منهما في سماعة أذن واحدة ما يقوله الآخر مترجمًا. يستخدم وضع وجهًا لوجه دائمًا جلسة مشتركة واحدة',
            'لغة المصدر ووضع الترجمة: يعمل "اكتشاف تلقائي" عند ترجمة صوتك أنت (وضع "أنا"). في وضع "الآخر" أو "الكل" اختر لغة مصدر محددة، إذ لا يمكن عكس الاكتشاف التلقائي لترجمة كلام الطرف الآخر إلى لغتك.',
            'لا حاجة لإعداد يدوي لكشف الأدوار — يكتشف Soniox نقاط نهاية الكلام على الخادم',
          ],
        },
      ],
    },
    troubleshooting: {
      title: 'استكشاف الأخطاء وإصلاحها',
      sections: [
        {
          title: 'المشاكل الشائعة',
          items: [
            { label: 'خطأ في المصادقة', content: 'تحقق من أن مفتاح API منسوخ بشكل صحيح بدون مسافات إضافية. إذا فُقد المفتاح، أنشئ مفتاحًا جديدًا في وحدة تحكم Soniox — تُعرض المفاتيح مرة واحدة فقط.' },
            { label: 'خطأ في الاتصال', content: 'تحقق من اتصالك بالشبكة وتأكد من أن جدران الحماية لا تحظر اتصالات WebSocket. إذا كنت تستخدم وكيلًا، تأكد من أنه يدعم WebSocket.' },
            { label: 'لا يوجد صوت مترجم', content: 'إذا كنت ترى الترجمة ولا تسمع شيئًا، فتحقق من قسم الكلام: يجب أن يكون خيار «الترجمة التي يسمعها الطرف الآخر» مفعّلًا. تذهب ترجمتك إلى الميكروفون الافتراضي ليسمعها الطرف الآخر؛ لتسمعها أنت أيضًا، فعّل «أسمعها أنا أيضًا» وتحقق من الجهاز في ذلك الصف.' },
          ],
        },
        {
          title: 'نصائح لأفضل النتائج',
          steps: [
            'استخدم اتصال شبكة مستقر للحصول على أفضل جودة فورية',
            'استخدم في بيئة هادئة للحصول على أفضل تعرف على الكلام',
            'حدد لغة المصدر صراحةً إذا اختار الكشف التلقائي لغة خاطئة',
            'راقب استخدامك ورصيدك في وحدة تحكم Soniox',
          ],
        },
      ],
    },
    helpText: 'هل تحتاج إلى مزيد من المساعدة؟ قم بزيارة وثائق Soniox لمزيد من التفاصيل أو راجع مستودع GitHub الخاص بنا للحصول على الدعم.',
    externalLinkText: 'افتح وحدة تحكم Soniox',
  },
  pt: {
    pageTitle: 'Tutorial de configuração do Soniox',
    backLink: 'Voltar aos provedores de IA',
    overview: {
      title: 'Visão geral',
      content: 'Este tutorial guiará você na configuração do Soniox para uso com o Sokuji. O Soniox é um serviço de tradução de voz para voz em tempo real que transcreve e traduz mais de 60 idiomas em uma única chamada de API de streaming.',
    },

    steps: [
      { title: 'Registre uma conta Soniox', content: 'Acesse <a href="https://console.soniox.com/signup" target="_blank" rel="noopener noreferrer">console.soniox.com/signup</a> e crie uma conta gratuita. Se você já tem uma conta Soniox, faça login diretamente em <a href="https://console.soniox.com" target="_blank" rel="noopener noreferrer">console.soniox.com</a>.' },
      { title: 'Abra o console', content: 'Após o login, abra o console do Soniox. Sua conta começa com um projeto padrão ("My First Project").' },
      { title: 'Vá para API Keys', content: 'No console, abra seu projeto e clique na seção <strong>API Keys</strong> na barra lateral.' },
      { title: 'Crie uma chave API', content: 'Clique em <strong>Criar nova chave</strong>, dê um nome a ela (por exemplo, "Sokuji") e confirme. Copie a chave imediatamente — ela é exibida apenas uma vez.' },
      { title: 'Configure no Sokuji', content: 'Abra o Sokuji e selecione <strong>Soniox</strong> como seu provedor de IA no painel de configurações. Cole sua <strong>chave API</strong>, escolha o idioma de origem (ou detecção automática) e o idioma de destino, e selecione uma voz. No bloco <strong>Voz</strong> (configurações rápidas, ou Configurações → Avançado → Áudio), <strong>Tradução que o outro lado ouve</strong> decide se a sua tradução é lida em voz alta ou mostrada só como texto.' },
      { title: 'Teste a conexão', content: 'Clique no botão Iniciar sessão e fale no microfone para verificar se a tradução em tempo real está funcionando. Você deverá ver a transcrição e a tradução. A tradução falada vai para o microfone virtual, para o outro lado; para ouvi-la também, ative <strong>Eu também ouço</strong> no bloco Voz.' },
    ],
    features: {
      title: 'Recursos',
      sections: [
        {
          title: 'O que é o Soniox?',
          content: 'O Soniox é um serviço de tradução de voz em tempo real de ponta a ponta:',
          items: [
            'Tradução de voz para voz em tempo real: transcrição, tradução e síntese de voz em um único pipeline',
            'Transcreve e traduz mais de 60 idiomas na mesma chamada de API de streaming, sem custo adicional pela tradução',
            'Detecção automática de idioma: defina a origem como detecção automática e o Soniox identifica o idioma falado',
            '200 vozes integradas para a tradução falada, e vozes que você clona a partir de uma gravação curta',
          ],
        },
        {
          title: 'Idiomas suportados',
          content: 'Suporta mais de 60 idiomas com tradução de qualquer idioma para qualquer outro:',
          items: [
            'O idioma de origem pode ser um idioma específico ou detecção automática',
            'O idioma de destino pode ser qualquer um dos mais de 60 idiomas',
            'Inclui chinês, inglês, japonês, coreano, espanhol, francês, alemão, italiano, português, russo e muitos mais',
          ],
        },
        {
          title: 'Modos e opções',
          items: [
            'Falada ou só texto: no bloco Voz, Tradução que o outro lado ouve lê a sua tradução em voz alta no microfone virtual, e Tradução que eu ouço lê para você a tradução do outro lado. Desative qualquer uma delas e esse lado fica só em texto',
            'Vozes: em Configurações → Avançado → Provedor → Configurações de Voz, escolha uma das 200 vozes integradas ou clone a sua. Enquanto Tradução que eu ouço estiver ativada, uma segunda linha, Voz que eu ouço, escolhe a voz que lê o outro lado para você: só vozes integradas, e se você escolher ali a sua própria voz, outra lê no lugar dela',
            'Tradução bidirecional: no modo "Ambos", cada direção usa por padrão a sua própria sessão do Soniox (Sessão compartilhada no modo Ambos: Desabilitado). Escolha Ativado para traduzir os dois lados em uma só sessão com separação automática de falantes, com menor custo e latência; duas sessões custam cerca do dobro, mas funcionam melhor quando as duas pessoas falam ao mesmo tempo',
            'Cara a cara: no modo "Ambos", defina Onde está o outro como Ao meu lado e duas pessoas compartilham um microfone; cada uma ouve em um fone o que a outra diz, traduzido. O cara a cara sempre usa uma sessão compartilhada',
            'Idioma de origem e modo de tradução: a detecção automática funciona ao traduzir a sua própria voz (modo "Eu"). Nos modos "Outro" ou "Ambos", escolha um idioma de origem específico — a detecção automática não pode ser invertida para traduzir o outro lado para o seu idioma.',
            'Sem configuração manual de detecção de turnos — o Soniox detecta os pontos finais da fala no servidor',
          ],
        },
      ],
    },
    troubleshooting: {
      title: 'Solução de problemas',
      sections: [
        {
          title: 'Problemas comuns',
          items: [
            { label: 'Erro de autenticação', content: 'Verifique se a chave API foi copiada corretamente sem espaços extras. Se a chave foi perdida, crie uma nova no console do Soniox — as chaves são exibidas apenas uma vez.' },
            { label: 'Erro de conexão', content: 'Verifique sua conexão de rede e certifique-se de que os firewalls não estão bloqueando conexões WebSocket. Se estiver usando um proxy, certifique-se de que ele suporta WebSocket.' },
            { label: 'Sem áudio traduzido', content: 'Se você vê a tradução mas não ouve nada, verifique o bloco Voz: Tradução que o outro lado ouve precisa estar ativada. A sua tradução vai para o microfone virtual, para o outro lado; para ouvi-la também, ative Eu também ouço e confira o dispositivo dessa linha.' },
          ],
        },
        {
          title: 'Dicas para melhores resultados',
          steps: [
            'Use uma conexão de rede estável para obter a melhor qualidade em tempo real',
            'Use em um ambiente silencioso para reconhecimento de voz ideal',
            'Defina o idioma de origem explicitamente se a detecção automática escolher o idioma errado',
            'Monitore seu uso e saldo no console do Soniox',
          ],
        },
      ],
    },
    helpText: 'Precisa de mais ajuda? Visite a documentação do Soniox para mais detalhes ou confira nosso repositório GitHub para suporte.',
    externalLinkText: 'Abrir o console do Soniox',
  },
  ru: {
    pageTitle: 'Руководство по настройке Soniox',
    backLink: 'Назад к провайдерам ИИ',
    overview: {
      title: 'Обзор',
      content: 'Это руководство поможет вам настроить Soniox для использования с Sokuji. Soniox — это сервис перевода речи в речь в реальном времени, который транскрибирует и переводит более 60 языков за один потоковый вызов API.',
    },

    steps: [
      { title: 'Зарегистрируйте аккаунт Soniox', content: 'Перейдите на <a href="https://console.soniox.com/signup" target="_blank" rel="noopener noreferrer">console.soniox.com/signup</a> и создайте бесплатный аккаунт. Если у вас уже есть аккаунт Soniox, войдите напрямую на <a href="https://console.soniox.com" target="_blank" rel="noopener noreferrer">console.soniox.com</a>.' },
      { title: 'Откройте консоль', content: 'После входа откройте консоль Soniox. Ваш аккаунт начинается с проекта по умолчанию («My First Project»).' },
      { title: 'Перейдите к API Keys', content: 'В консоли откройте свой проект и нажмите раздел <strong>API Keys</strong> на боковой панели.' },
      { title: 'Создайте ключ API', content: 'Нажмите <strong>Создать новый ключ</strong>, дайте ему имя (например, «Sokuji») и подтвердите. Скопируйте ключ сразу — он отображается только один раз.' },
      { title: 'Настройте в Sokuji', content: 'Откройте Sokuji и выберите <strong>Soniox</strong> в качестве провайдера ИИ в панели настроек. Вставьте свой <strong>ключ API</strong>, выберите исходный язык (или автоопределение) и целевой язык, а также голос. В блоке <strong>Речь</strong> (быстрые настройки или Настройки → Расширенный → Аудио) переключатель <strong>Перевод, который слышит собеседник</strong> решает, будет ли ваш перевод озвучен или показан только текстом.' },
      { title: 'Протестируйте соединение', content: 'Нажмите кнопку «Начать сеанс» и говорите в микрофон, чтобы убедиться, что перевод в реальном времени работает. Вы должны увидеть транскрипцию и перевод. Озвученный перевод уходит в виртуальный микрофон для собеседника; чтобы слышать его самому, включите <strong>Я тоже слышу</strong> в блоке «Речь».' },
    ],
    features: {
      title: 'Функции',
      sections: [
        {
          title: 'Что такое Soniox?',
          content: 'Soniox — это сквозной сервис перевода речи в реальном времени:',
          items: [
            'Перевод речи в речь в реальном времени: транскрипция, перевод и синтез речи в одном конвейере',
            'Транскрибирует и переводит более 60 языков в одном потоковом вызове API, без дополнительной платы за перевод',
            'Автоматическое определение языка: установите источник на автоопределение, и Soniox определит язык речи',
            '200 встроенных голосов для озвучивания перевода, а также голоса, клонированные по короткой записи',
          ],
        },
        {
          title: 'Поддерживаемые языки',
          content: 'Поддерживает более 60 языков с переводом с любого языка на любой другой:',
          items: [
            'Исходный язык может быть конкретным языком или автоопределением',
            'Целевым языком может быть любой из более чем 60 языков',
            'Включает китайский, английский, японский, корейский, испанский, французский, немецкий, итальянский, португальский, русский и многие другие',
          ],
        },
        {
          title: 'Режимы и параметры',
          items: [
            'Озвучка или только текст: в блоке «Речь» переключатель «Перевод, который слышит собеседник» озвучивает ваш перевод в виртуальный микрофон, а «Перевод, который слышу я» озвучивает вам перевод собеседника. Выключите любой из них — и эта сторона останется только текстом',
            'Голоса: в Настройки → Расширенный → Поставщик → Настройки голоса выберите один из 200 встроенных голосов или клонируйте свой. Пока включён «Перевод, который слышу я», появляется вторая строка «Голос, который слышу я» — голос, которым вам читается перевод собеседника: только встроенные голоса, а если выбрать там свой собственный голос, вместо него будет читать другой',
            'Двунаправленный перевод: в режиме «Оба» каждое направление по умолчанию работает в собственном сеансе Soniox (Общий сеанс в режиме «Оба»: Отключено). Выберите «Включено», чтобы переводить обе стороны в одном сеансе с автоматическим разделением говорящих — дешевле и с меньшей задержкой; два сеанса стоят примерно вдвое дороже, но надёжнее, когда оба говорят одновременно',
            'Лицом к лицу: в режиме «Оба» установите «Где собеседник» на «Рядом со мной» — два человека делят один микрофон, и каждый слышит в одном наушнике перевод того, что говорит другой. В режиме «Лицом к лицу» всегда используется один общий сеанс',
            'Исходный язык и режим перевода: автоопределение работает при переводе вашего собственного голоса (режим «Я»). В режимах «Собеседник» или «Оба» выберите конкретный исходный язык — автоопределение нельзя развернуть в обратную сторону, чтобы переводить собеседника на ваш язык.',
            'Не требуется ручная настройка определения очереди — Soniox определяет конечные точки речи на сервере',
          ],
        },
      ],
    },
    troubleshooting: {
      title: 'Устранение неполадок',
      sections: [
        {
          title: 'Частые проблемы',
          items: [
            { label: 'Ошибка аутентификации', content: 'Убедитесь, что ключ API скопирован правильно, без лишних пробелов. Если ключ утерян, создайте новый в консоли Soniox — ключи отображаются только один раз.' },
            { label: 'Ошибка подключения', content: 'Проверьте сетевое подключение и убедитесь, что брандмауэры не блокируют WebSocket-соединения. Если используете прокси, убедитесь, что он поддерживает WebSocket.' },
            { label: 'Нет переведённого звука', content: 'Если вы видите перевод, но ничего не слышите, проверьте блок «Речь»: «Перевод, который слышит собеседник» должен быть включён. Ваш перевод уходит в виртуальный микрофон для собеседника; чтобы слышать его самому, включите «Я тоже слышу» и проверьте устройство в этой строке.' },
          ],
        },
        {
          title: 'Советы для лучших результатов',
          steps: [
            'Используйте стабильное сетевое подключение для наилучшего качества в реальном времени',
            'Используйте в тихой обстановке для оптимального распознавания речи',
            'Явно укажите исходный язык, если автоопределение выбирает неправильный язык',
            'Отслеживайте использование и баланс в консоли Soniox',
          ],
        },
      ],
    },
    helpText: 'Нужна дополнительная помощь? Посетите документацию Soniox для получения подробностей или наш репозиторий GitHub для получения поддержки.',
    externalLinkText: 'Открыть консоль Soniox',
  },
  de: {
    pageTitle: 'Soniox Einrichtungstutorial',
    backLink: 'Zurück zu KI-Anbietern',
    overview: {
      title: 'Übersicht',
      content: 'Dieses Tutorial führt Sie durch die Einrichtung von Soniox für die Verwendung mit Sokuji. Soniox ist ein Echtzeit-Sprache-zu-Sprache-Übersetzungsdienst, der über 60 Sprachen in einem einzigen Streaming-API-Aufruf transkribiert und übersetzt.',
    },

    steps: [
      { title: 'Registrieren Sie ein Soniox-Konto', content: 'Gehen Sie zu <a href="https://console.soniox.com/signup" target="_blank" rel="noopener noreferrer">console.soniox.com/signup</a> und erstellen Sie ein kostenloses Konto. Wenn Sie bereits ein Soniox-Konto haben, melden Sie sich direkt unter <a href="https://console.soniox.com" target="_blank" rel="noopener noreferrer">console.soniox.com</a> an.' },
      { title: 'Öffnen Sie die Konsole', content: 'Öffnen Sie nach der Anmeldung die Soniox-Konsole. Ihr Konto beginnt mit einem Standardprojekt („My First Project“).' },
      { title: 'Gehen Sie zu API Keys', content: 'Öffnen Sie in der Konsole Ihr Projekt und klicken Sie in der Seitenleiste auf den Abschnitt <strong>API Keys</strong>.' },
      { title: 'Erstellen Sie einen API-Schlüssel', content: 'Klicken Sie auf <strong>Neuen Schlüssel erstellen</strong>, geben Sie ihm einen Namen (z. B. „Sokuji“) und bestätigen Sie. Kopieren Sie den Schlüssel sofort — er wird nur einmal angezeigt.' },
      { title: 'Konfigurieren Sie in Sokuji', content: 'Öffnen Sie Sokuji und wählen Sie <strong>Soniox</strong> als Ihren KI-Anbieter im Einstellungspanel. Fügen Sie Ihren <strong>API-Schlüssel</strong> ein, wählen Sie die Quellsprache (oder automatische Erkennung) und die Zielsprache sowie eine Stimme. Im Block <strong>Sprache</strong> (Schnell-Einstellungen oder Einstellungen → Erweitert → Audio) entscheidet <strong>Übersetzung, die das Gegenüber hört</strong>, ob Ihre Übersetzung vorgelesen oder nur als Text angezeigt wird.' },
      { title: 'Testen Sie die Verbindung', content: 'Klicken Sie auf die Schaltfläche Sitzung starten und sprechen Sie in Ihr Mikrofon, um zu überprüfen, ob die Echtzeitübersetzung funktioniert. Sie sollten die Transkription und die Übersetzung sehen. Die gesprochene Übersetzung geht für das Gegenüber ins virtuelle Mikrofon; um sie selbst zu hören, schalten Sie im Block Sprache <strong>Ich höre es auch</strong> ein.' },
    ],
    features: {
      title: 'Funktionen',
      sections: [
        {
          title: 'Was ist Soniox?',
          content: 'Soniox ist ein durchgängiger Echtzeit-Sprachübersetzungsdienst:',
          items: [
            'Echtzeit-Sprache-zu-Sprache-Übersetzung: Transkription, Übersetzung und Sprachsynthese in einer Pipeline',
            'Transkribiert und übersetzt über 60 Sprachen im selben Streaming-API-Aufruf, ohne Zusatzkosten für die Übersetzung',
            'Automatische Spracherkennung: Stellen Sie die Quelle auf automatische Erkennung und Soniox identifiziert die gesprochene Sprache',
            '200 integrierte Stimmen für die gesprochene Übersetzung sowie Stimmen, die Sie aus einer kurzen Aufnahme klonen',
          ],
        },
        {
          title: 'Unterstützte Sprachen',
          content: 'Unterstützt über 60 Sprachen mit Übersetzung von jeder Sprache in jede andere:',
          items: [
            'Die Quellsprache kann eine bestimmte Sprache oder die automatische Erkennung sein',
            'Die Zielsprache kann eine der über 60 Sprachen sein',
            'Umfasst Chinesisch, Englisch, Japanisch, Koreanisch, Spanisch, Französisch, Deutsch, Italienisch, Portugiesisch, Russisch und viele mehr',
          ],
        },
        {
          title: 'Modi und Optionen',
          items: [
            'Gesprochen oder nur Text: Im Block Sprache liest Übersetzung, die das Gegenüber hört, Ihre Übersetzung ins virtuelle Mikrofon vor, und Übersetzung, die ich höre, liest Ihnen die Übersetzung des Gegenübers vor. Schalten Sie einen der beiden aus, bleibt diese Seite nur Text',
            'Stimmen: Wählen Sie unter Einstellungen → Erweitert → Anbieter → Stimmeinstellungen eine der 200 integrierten Stimmen oder klonen Sie Ihre eigene. Solange Übersetzung, die ich höre, eingeschaltet ist, wählt eine zweite Zeile, Stimme, die ich höre, die Stimme, die Ihnen das Gegenüber vorliest: nur integrierte Stimmen, und wählen Sie dort Ihre eigene Stimme, liest stattdessen eine andere',
            'Bidirektionale Übersetzung: Im Modus „Beides“ läuft jede Richtung standardmäßig in einer eigenen Soniox-Sitzung (Gemeinsame Sitzung im Modus „Beides“: Deaktiviert). Wählen Sie Aktiviert, um beide Seiten in einer Sitzung mit automatischer Sprechertrennung zu übersetzen, günstiger und mit geringerer Latenz; zwei Sitzungen kosten etwa doppelt so viel, kommen aber besser damit zurecht, wenn beide gleichzeitig sprechen',
            'Vor Ort: Stellen Sie im Modus „Beides“ Wo ist das Gegenüber? auf Neben mir; dann teilen sich zwei Personen ein Mikrofon, und jede hört in einem Ohrhörer, was die andere sagt, übersetzt. Vor Ort verwendet immer eine gemeinsame Sitzung',
            'Quellsprache und Übersetzungsmodus: Die automatische Erkennung funktioniert beim Übersetzen Ihrer eigenen Stimme (Modus „Ich“). Wählen Sie im Modus „Gegenüber“ oder „Beides“ eine konkrete Quellsprache — die automatische Erkennung lässt sich nicht umkehren, um das Gegenüber in Ihre Sprache zu übersetzen.',
            'Keine manuelle Konfiguration der Sprechererkennung — Soniox erkennt die Sprachendpunkte serverseitig',
          ],
        },
      ],
    },
    troubleshooting: {
      title: 'Fehlerbehebung',
      sections: [
        {
          title: 'Häufige Probleme',
          items: [
            { label: 'Authentifizierungsfehler', content: 'Überprüfen Sie, ob Ihr API-Schlüssel korrekt und ohne zusätzliche Leerzeichen kopiert wurde. Wenn der Schlüssel verloren ging, erstellen Sie einen neuen in der Soniox-Konsole — Schlüssel werden nur einmal angezeigt.' },
            { label: 'Verbindungsfehler', content: 'Überprüfen Sie Ihre Netzwerkverbindung und stellen Sie sicher, dass Firewalls WebSocket-Verbindungen nicht blockieren. Bei Verwendung eines Proxys stellen Sie sicher, dass er WebSocket unterstützt.' },
            { label: 'Kein übersetztes Audio', content: 'Wenn Sie die Übersetzung sehen, aber nichts hören, prüfen Sie den Block Sprache: Übersetzung, die das Gegenüber hört, muss eingeschaltet sein. Ihre Übersetzung geht für das Gegenüber ins virtuelle Mikrofon; um sie selbst zu hören, schalten Sie Ich höre es auch ein und prüfen Sie das Gerät in dieser Zeile.' },
          ],
        },
        {
          title: 'Tipps für beste Ergebnisse',
          steps: [
            'Verwenden Sie eine stabile Netzwerkverbindung für die beste Echtzeitqualität',
            'Verwenden Sie es in einer ruhigen Umgebung für optimale Spracherkennung',
            'Legen Sie die Quellsprache explizit fest, wenn die automatische Erkennung die falsche Sprache wählt',
            'Überwachen Sie Ihre Nutzung und Ihr Guthaben in der Soniox-Konsole',
          ],
        },
      ],
    },
    helpText: 'Benötigen Sie weitere Hilfe? Besuchen Sie die Soniox-Dokumentation für Details oder unser GitHub-Repository für Support.',
    externalLinkText: 'Soniox-Konsole öffnen',
  },
  uk: {
    pageTitle: 'Посібник з налаштування Soniox',
    backLink: 'Назад до провайдерів ШІ',
    overview: {
      title: 'Огляд',
      content: 'Цей посібник допоможе вам налаштувати Soniox для використання з Sokuji. Soniox — це сервіс перекладу мовлення в мовлення в реальному часі, який транскрибує та перекладає понад 60 мов за один потоковий виклик API.',
    },

    steps: [
      { title: 'Зареєструйте обліковий запис Soniox', content: 'Перейдіть на <a href="https://console.soniox.com/signup" target="_blank" rel="noopener noreferrer">console.soniox.com/signup</a> та створіть безкоштовний обліковий запис. Якщо у вас вже є обліковий запис Soniox, увійдіть напряму на <a href="https://console.soniox.com" target="_blank" rel="noopener noreferrer">console.soniox.com</a>.' },
      { title: 'Відкрийте консоль', content: 'Після входу відкрийте консоль Soniox. Ваш обліковий запис починається з проєкту за замовчуванням («My First Project»).' },
      { title: 'Перейдіть до API Keys', content: 'У консолі відкрийте свій проєкт і натисніть розділ <strong>API Keys</strong> на бічній панелі.' },
      { title: 'Створіть ключ API', content: 'Натисніть <strong>Створити новий ключ</strong>, дайте йому назву (наприклад, «Sokuji») та підтвердіть. Скопіюйте ключ одразу — він показується лише один раз.' },
      { title: 'Налаштуйте в Sokuji', content: 'Відкрийте Sokuji та оберіть <strong>Soniox</strong> як свого провайдера ШІ в панелі налаштувань. Вставте свій <strong>ключ API</strong>, оберіть мову джерела (або автовизначення) та цільову мову, а також голос. У блоці <strong>Мовлення</strong> (швидкі налаштування або Налаштування → Розширений → Аудіо) перемикач <strong>Переклад, який чує співрозмовник</strong> вирішує, буде ваш переклад озвучено чи показано лише текстом.' },
      { title: 'Протестуйте з\'єднання', content: 'Натисніть кнопку «Почати сеанс» та говоріть у мікрофон, щоб перевірити, що переклад у реальному часі працює. Ви повинні побачити транскрипцію та переклад. Озвучений переклад іде у віртуальний мікрофон для співрозмовника; щоб чути його самому, увімкніть <strong>Я теж чую</strong> у блоці «Мовлення».' },
    ],
    features: {
      title: 'Функції',
      sections: [
        {
          title: 'Що таке Soniox?',
          content: 'Soniox — це наскрізний сервіс перекладу мовлення в реальному часі:',
          items: [
            'Переклад мовлення в мовлення в реальному часі: транскрипція, переклад і синтез мовлення в одному конвеєрі',
            'Транскрибує та перекладає понад 60 мов у тому самому потоковому виклику API, без додаткової плати за переклад',
            'Автоматичне визначення мови: встановіть джерело на автовизначення, і Soniox визначить мову мовлення',
            '200 вбудованих голосів для озвучення перекладу, а також голоси, клоновані з короткого запису',
          ],
        },
        {
          title: 'Підтримувані мови',
          content: 'Підтримує понад 60 мов з перекладом з будь-якої мови на будь-яку іншу:',
          items: [
            'Мовою джерела може бути конкретна мова або автовизначення',
            'Цільовою мовою може бути будь-яка з понад 60 мов',
            'Включає китайську, англійську, японську, корейську, іспанську, французьку, німецьку, італійську, португальську, російську та багато інших',
          ],
        },
        {
          title: 'Режими та параметри',
          items: [
            'Озвучення або лише текст: у блоці «Мовлення» перемикач «Переклад, який чує співрозмовник» озвучує ваш переклад у віртуальний мікрофон, а «Переклад, який чую я» озвучує вам переклад співрозмовника. Вимкніть будь-який із них — і ця сторона залишиться лише текстом',
            'Голоси: у Налаштування → Розширений → Постачальник → Налаштування голосу оберіть один із 200 вбудованих голосів або клонуйте свій. Поки ввімкнено «Переклад, який чую я», з’являється другий рядок «Голос, який чую я» — голос, яким вам читається переклад співрозмовника: лише вбудовані голоси, а якщо обрати там свій власний голос, замість нього читатиме інший',
            'Двонаправлений переклад: у режимі «Обидва» кожен напрямок за замовчуванням працює у власному сеансі Soniox (Спільний сеанс у режимі «Обидва»: Вимкнено). Оберіть «Увімкнено», щоб перекладати обидві сторони в одному сеансі з автоматичним розділенням мовців — дешевше та з меншою затримкою; два сеанси коштують приблизно вдвічі дорожче, але надійніші, коли обоє говорять одночасно',
            'Віч-на-віч: у режимі «Обидва» встановіть «Де співрозмовник» на «Поруч зі мною» — двоє людей ділять один мікрофон, і кожен чує в одному навушнику переклад того, що говорить інший. Режим «Віч-на-віч» завжди використовує один спільний сеанс',
            'Мова джерела та режим перекладу: автовизначення працює під час перекладу вашого власного голосу (режим «Я»). У режимах «Співрозмовник» або «Обидва» оберіть конкретну мову джерела — автовизначення не можна розвернути у зворотний бік, щоб перекладати співрозмовника вашою мовою.',
            'Не потрібне ручне налаштування виявлення черги — Soniox визначає кінцеві точки мовлення на сервері',
          ],
        },
      ],
    },
    troubleshooting: {
      title: 'Усунення несправностей',
      sections: [
        {
          title: 'Поширені проблеми',
          items: [
            { label: 'Помилка автентифікації', content: 'Переконайтеся, що ключ API скопійовано правильно, без зайвих пробілів. Якщо ключ втрачено, створіть новий у консолі Soniox — ключі показуються лише один раз.' },
            { label: 'Помилка з\'єднання', content: 'Перевірте мережеве підключення та переконайтеся, що брандмауери не блокують WebSocket-з\'єднання. Якщо використовуєте проксі, переконайтеся, що він підтримує WebSocket.' },
            { label: 'Немає перекладеного звуку', content: 'Якщо ви бачите переклад, але нічого не чуєте, перевірте блок «Мовлення»: «Переклад, який чує співрозмовник» має бути ввімкнено. Ваш переклад іде у віртуальний мікрофон для співрозмовника; щоб чути його самому, увімкніть «Я теж чую» і перевірте пристрій у цьому рядку.' },
          ],
        },
        {
          title: 'Поради для найкращих результатів',
          steps: [
            'Використовуйте стабільне мережеве підключення для найкращої якості в реальному часі',
            'Використовуйте в тихому середовищі для оптимального розпізнавання мовлення',
            'Явно вкажіть мову джерела, якщо автовизначення обирає неправильну мову',
            'Відстежуйте використання та баланс у консолі Soniox',
          ],
        },
      ],
    },
    helpText: 'Потрібна додаткова допомога? Відвідайте документацію Soniox для деталей або наш репозиторій GitHub для підтримки.',
    externalLinkText: 'Відкрити консоль Soniox',
  },
  it: {
    pageTitle: 'Tutorial di configurazione Soniox',
    backLink: 'Torna ai fornitori di IA',
    overview: {
      title: 'Panoramica',
      content: 'Questo tutorial ti guiderà nella configurazione di Soniox per l\'uso con Sokuji. Soniox è un servizio di traduzione voce-a-voce in tempo reale che trascrive e traduce oltre 60 lingue in una singola chiamata API di streaming.',
    },

    steps: [
      { title: 'Registra un account Soniox', content: 'Vai su <a href="https://console.soniox.com/signup" target="_blank" rel="noopener noreferrer">console.soniox.com/signup</a> e crea un account gratuito. Se hai già un account Soniox, accedi direttamente su <a href="https://console.soniox.com" target="_blank" rel="noopener noreferrer">console.soniox.com</a>.' },
      { title: 'Apri la console', content: 'Dopo l\'accesso, apri la console Soniox. Il tuo account inizia con un progetto predefinito ("My First Project").' },
      { title: 'Vai su API Keys', content: 'Nella console, apri il tuo progetto e clicca sulla sezione <strong>API Keys</strong> nella barra laterale.' },
      { title: 'Crea una chiave API', content: 'Clicca su <strong>Crea nuova chiave</strong>, assegnale un nome (ad es. "Sokuji") e conferma. Copia la chiave immediatamente — viene mostrata una sola volta.' },
      { title: 'Configura in Sokuji', content: 'Apri Sokuji e seleziona <strong>Soniox</strong> come fornitore di IA nel pannello impostazioni. Incolla la tua <strong>chiave API</strong>, scegli la lingua di origine (o rilevamento automatico) e la lingua di destinazione, e seleziona una voce. Nel blocco <strong>Voce</strong> (impostazioni rapide, oppure Impostazioni → Avanzato → Audio), <strong>Traduzione che sente l\'altra parte</strong> decide se la tua traduzione viene letta ad alta voce o mostrata solo come testo.' },
      { title: 'Testa la connessione', content: 'Clicca il pulsante Avvia sessione e parla nel microfono per verificare che la traduzione in tempo reale funzioni. Dovresti vedere la trascrizione e la traduzione. La traduzione parlata va nel microfono virtuale per l\'altra parte; per sentirla anche tu, attiva <strong>Lo sento anche io</strong> nel blocco Voce.' },
    ],
    features: {
      title: 'Funzionalità',
      sections: [
        {
          title: 'Cos\'è Soniox?',
          content: 'Soniox è un servizio di traduzione vocale in tempo reale end-to-end:',
          items: [
            'Traduzione voce-a-voce in tempo reale: trascrizione, traduzione e sintesi vocale in un\'unica pipeline',
            'Trascrive e traduce oltre 60 lingue nella stessa chiamata API di streaming, senza costi aggiuntivi per la traduzione',
            'Rilevamento automatico della lingua: imposta l\'origine su rilevamento automatico e Soniox identifica la lingua parlata',
            '200 voci integrate per la traduzione parlata, e voci che cloni da una breve registrazione',
          ],
        },
        {
          title: 'Lingue supportate',
          content: 'Supporta oltre 60 lingue con traduzione da qualsiasi lingua a qualsiasi altra:',
          items: [
            'La lingua di origine può essere una lingua specifica o il rilevamento automatico',
            'La lingua di destinazione può essere una qualsiasi delle oltre 60 lingue',
            'Include cinese, inglese, giapponese, coreano, spagnolo, francese, tedesco, italiano, portoghese, russo e molte altre',
          ],
        },
        {
          title: 'Modalità e opzioni',
          items: [
            'Parlata o solo testo: nel blocco Voce, Traduzione che sente l\'altra parte legge la tua traduzione ad alta voce nel microfono virtuale, e Traduzione che sento io ti legge la traduzione dell\'altra parte. Disattiva una delle due e quel lato resta solo testo',
            'Voci: in Impostazioni → Avanzato → Fornitore → Impostazioni Voce, scegli una delle 200 voci integrate o clona la tua. Finché Traduzione che sento io è attiva, una seconda riga, Voce che sento io, sceglie la voce che ti legge l\'altra parte: solo voci integrate, e se lì scegli la tua stessa voce, ne legge un\'altra al suo posto',
            'Traduzione bidirezionale: in modalità "Entrambi" ogni direzione usa per impostazione predefinita una propria sessione Soniox (Sessione condivisa in modalità Entrambi: Disabilitato). Scegli Abilitato per tradurre entrambe le parti in un\'unica sessione con separazione automatica dei parlanti, con costi e latenza minori; due sessioni costano circa il doppio ma reggono meglio quando entrambe le persone parlano insieme',
            'Faccia a faccia: in modalità "Entrambi", imposta Dov\'è l\'altro su Accanto a me; due persone condividono un microfono e ciascuna sente in un auricolare ciò che dice l\'altra, tradotto. Il faccia a faccia usa sempre una sessione condivisa',
            'Lingua di origine e modalità di traduzione: il rilevamento automatico funziona quando traduci la tua voce (modalità "Io"). In modalità "Altro" o "Entrambi", scegli una lingua di origine precisa — il rilevamento automatico non può essere invertito per tradurre l\'altra parte nella tua lingua.',
            'Nessuna configurazione manuale del rilevamento dei turni — Soniox rileva i punti finali del parlato lato server',
          ],
        },
      ],
    },
    troubleshooting: {
      title: 'Risoluzione dei problemi',
      sections: [
        {
          title: 'Problemi comuni',
          items: [
            { label: 'Errore di autenticazione', content: 'Verifica che la chiave API sia copiata correttamente senza spazi aggiuntivi. Se la chiave è andata persa, creane una nuova nella console Soniox — le chiavi vengono mostrate una sola volta.' },
            { label: 'Errore di connessione', content: 'Controlla la connessione di rete e assicurati che i firewall non blocchino le connessioni WebSocket. Se usi un proxy, assicurati che supporti WebSocket.' },
            { label: 'Nessun audio tradotto', content: 'Se vedi la traduzione ma non senti nulla, controlla il blocco Voce: Traduzione che sente l\'altra parte deve essere attiva. La tua traduzione va nel microfono virtuale per l\'altra parte; per sentirla anche tu, attiva Lo sento anche io e controlla il dispositivo di quella riga.' },
          ],
        },
        {
          title: 'Suggerimenti per i migliori risultati',
          steps: [
            'Usa una connessione di rete stabile per la migliore qualità in tempo reale',
            'Usa in un ambiente silenzioso per un riconoscimento vocale ottimale',
            'Imposta esplicitamente la lingua di origine se il rilevamento automatico sceglie la lingua sbagliata',
            'Monitora l\'utilizzo e il saldo nella console Soniox',
          ],
        },
      ],
    },
    helpText: 'Hai bisogno di ulteriore aiuto? Visita la documentazione Soniox per i dettagli o il nostro repository GitHub per supporto.',
    externalLinkText: 'Apri la console Soniox',
  },
};

export function SonioxSetup() {
  const { locale } = useI18n();
  const t = translations[locale] || translations.en;
  useDocumentMeta(t.pageTitle, t.overview.content);

  return (
    <div className="docs-content tutorial-page">
      <Link to={localizePath('/docs/ai-providers', locale)} className="tutorial-page__back-link">
        <ArrowLeft size={16} />
        {t.backLink}
      </Link>

      <h1>{t.pageTitle}</h1>

      <section className="tutorial-page__section">
        <h2>{t.overview.title}</h2>
        <p>{t.overview.content}</p>
      </section>

      <section className="tutorial-page__section">
        {t.steps.map((step, index) => (
          <div key={index} className="tutorial-page__step" style={{ borderLeftColor: ACCENT_COLOR }}>
            <h3>
              <span className="tutorial-page__step-number" style={{ backgroundColor: ACCENT_COLOR }}>{index + 1}</span>
              {step.title}
            </h3>
            <p dangerouslySetInnerHTML={{ __html: step.content }} />
          </div>
        ))}
      </section>

      <section className="tutorial-page__section">
        <h2>{t.features.title}</h2>
        {t.features.sections.map((section, index) => (
          <div key={index} className="tutorial-page__step" style={{ borderLeftColor: ACCENT_COLOR }}>
            <h3>{section.title}</h3>
            {section.content && <p>{section.content}</p>}
            <ul>
              {section.items.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      <section className="tutorial-page__section">
        <h2>{t.troubleshooting.title}</h2>
        {t.troubleshooting.sections.map((section, index) => (
          <div key={index} className="tutorial-page__step" style={{ borderLeftColor: ACCENT_COLOR }}>
            <h3>{section.title}</h3>
            {section.items && section.items.map((item, i) => (
              <p key={i}><strong>{item.label}:</strong> {item.content}</p>
            ))}
            {section.steps && (
              <ol>
                {section.steps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            )}
          </div>
        ))}
      </section>

      <section className="tutorial-page__section">
        <div className="tutorial-page__tip">
          <p>{t.helpText}</p>
        </div>
      </section>

      <section className="tutorial-page__section">
        <a
          href="https://console.soniox.com"
          target="_blank"
          rel="noopener noreferrer"
          className="install-page__download-btn"
          style={{ backgroundColor: ACCENT_COLOR }}
        >
          {t.externalLinkText}
          <ExternalLink size={16} />
        </a>
      </section>
    </div>
  );
}
